import { Button, Container, Loader, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { withSuspenseBoundary } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useOrganization } from '@/hooks/useOrganization';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { CreateLimitedAccessOrganization } from '@/pages/shared/components/plot-group/CreateLimitedAccessOrganization';
import { CreatePlotGroup } from '@/pages/shared/components/plot-group/CreatePlotGroup';
import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { PlotListDesktop } from '@/pages/shared/plots/components/PlotListDesktop';
import { useDefaultCustomCells } from '@/pages/shared/plots/hooks/useGetCustomCells';
import { ProjectsMap } from '@/pages/shared/projects/components/projectsMap/ProjectsMap';
import { AddPlotsModal } from '@/pages/shared/staff-portal/components/AddPlotsModal';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

const PlotsList = withSuspenseBoundary(
  () => {
    const { organizationId } = useParams();

    const customColumns = useDefaultCustomCells();
    return (
      <>
        <PlotListDesktop
          organizationIdArg={organizationId}
          customColumns={customColumns}
          enableRowSelection={true}
          showSort={false}
          showDraftPlots={true}
        />
      </>
    );
  },
  <Loader className='min-h-[300px]' />,
);

export const OrganizationPage = () => {
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const organization = useOrganization({ id: organizationId }).data.results[0];

  const setOrganizationId = useSetOrganizationId();
  useEffect(() => {
    if (!isNullish(organizationId)) {
      setOrganizationId(organizationId);
    }
  }, [organizationId, setOrganizationId]);

  const [searchParams] = useSearchParams();
  const plotGroupId = searchParams.get('plotGroupId');
  const [addPlotsOpen, setAddPlotsOpen] = useState(false);

  if (isNullish(organization)) throw new Error(`Unknown organization with id ${organizationId}`);

  return (
    <>
      <SignedInPageLayout
        items={[]}
        withCustomContentContainer
        navBarProps={{
          title: organization.name,
          backPath: buildPath(paths.staff.organizations),
          showFilters: true,
          children: (
            <Button
              variant='outline'
              leftAdornment={<Icon icon='ri-upload-cloud-2-line' />}
              onClick={() => setAddPlotsOpen(true)}
              data-testid='create-plots-button'
            >
              {t('shared.organizations.actions.createPlots')}
            </Button>
          ),
        }}
      >
        <Container gutterWidth={{ sm: 4 }} className='gap-10'>
          {isNullish(plotGroupId) && !isNullish(organization.id) && (
            <Stack direction='row' className='justify-end gap-4'>
              <CreatePlotGroup organizationId={organization.id} />
              <CreateLimitedAccessOrganization organizationId={organization.id} />
            </Stack>
          )}
          <div data-testid='org-map' className='h-[60vh] animate-in fade-in'>
            <ProjectsMap organizationIdArg={organization.id} />
          </div>
          <PlotsList data-testid='org-plot-list' />
        </Container>
      </SignedInPageLayout>
      {!isNullish(organizationId) && (
        <AddPlotsModal open={addPlotsOpen} onOpenChange={setAddPlotsOpen} organizationId={organizationId} />
      )}
    </>
  );
};
